import {
  ActionDisplayType,
  ActionHeader,
  BaseActionType,
  EntityActionType,
  isBaseActionType,
  NavigateActionHeader,
  NavigateType,
  newActionHeader,
} from '@decisions/server/DecisionsFramework/ServiceLayer/Actions';
import { IActionLinkItem, IActionLinkItems, IActionLinksPair } from '../components/ActionLinks/ActionLinkModels';
import {
  getActionByClientActionId,
  getActionsForAnyEntity,
} from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Folder/FolderService';
import { getActionHeadersForAnyEntity } from '@decisions/server/DecisionsFramework/ServiceLayer/Actions/Common/CommonActionService';
import { GroupAction } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Folder';
import { getFolderId, usePortalStore } from '../domains/portal/PortalStore';
import { stringContains } from './StringUtils';
import {
  getEntityDetailsDto,
  getEntityDetailsDtoForMobile,
} from '@decisions/server/DecisionsFramework/ServiceLayer/Services/EntityDetails/EntityDetailsService';
import { getIsMobile } from 'config/PortalConfiguration';
import {
  EntityActionInfo,
  newEntityActionInfo,
} from '@decisions/server/DecisionsFramework/Utilities/Messaging/EntityActionInfo';
import { newActionMessage } from '@decisions/server/DecisionsFramework/Utilities/Messaging/ActionMessage';

export function createSeparator(order: number): ActionHeader & { IsDummy: boolean } {
  return {
    ...newActionHeader({
      Flg: 'SEPERATOR',
      Id: 'no action',
      Nm: '',
      Or: order,
      Tp: 'NoAction',
      Tr: false, // translate
      // NTNM: undefined, // NonTranslatedName
      // HT: undefined, // Supportable handlers
    }),
    ...{ IsDummy: true },
  };
}

export function removeCategories<T extends ActionHeader | BaseActionType>(actions: T[]): T[] {
  return actions.map((original: T) => ({ ...original, Ca: '', Category: '' }));
}

export function hideExistingMenu() {
  Decisions.ActionHelper.HideMenu(
    Decisions.ActionHelper.LastMouseClickObject == null ? { which: 3 } : Decisions.ActionHelper.LastMouseClickObject
  );
}

/** TODO we are introducing duplicates of these. Need to consolidate */
export function handleRunDefaultAction(
  instanceId: string,
  data: any,
  entityId: string | undefined,
  targetElement: Element | undefined
) {
  if (entityId && instanceId && data) {
    $DP.Common.Actions.ActionExecutor.openAction(
      targetElement as HTMLElement,
      encodeURI(data.Name),
      encodeURI(entityId),
      data.ActionUniqueName
    );
  }
}

export const ACTION_ID_SEPARATOR = '|!~|';
export const ACTIONS_FOR_ACTIONS_HEADER_TYPE = 'DecisionsFramework.ServiceLayer.Actions.ActionsForActionHeader';

// TODO reference/compare MVC actionutils.ts & serveractionheader.ts
// TODO consolidate this with src/utils/ActionUtils.ts

/**
 *
 * @param entityId id of entity whose actions to load
 * @param typeName Decisions entity type-name string
 */
export async function fetchActions(entityId: string, typeName: string, abortController?: AbortController) {
  try {
    const actions = await getActionsForAnyEntity(
      {
        actionTypes: [EntityActionType.Mvc],
        typeName,
        entityID: entityId,
      },
      abortController
    );

    return actions;
  } catch (err) {
    console.error('Error fetching actions', err);
  }
}

export async function getEntityActionDetails(entityId: string | undefined, typeName: string | undefined) {
  if (getIsMobile()) {
    const entityDetailsForMobile = await getEntityDetailsDtoForMobile({ entityId, typeName });
    return entityDetailsForMobile?.Actions;
  } else {
    const entityDetailsDto = await getEntityDetailsDto({ entityId, typeName });
    return entityDetailsDto?.Actions;
  }
}

export async function getEntityDetails(entityId: string | undefined, typeName: string | undefined) {
  if (getIsMobile()) {
    const entityDetailsForMobile = await getEntityDetailsDtoForMobile({ entityId, typeName });
    return entityDetailsForMobile;
  } else {
    const entityDetailsDto = await getEntityDetailsDto({ entityId, typeName });
    return entityDetailsDto;
  }
}

export async function executeFavoriteActionOnAction(action: BaseActionType | ActionHeader, el: HTMLElement) {
  const entityId = getEntityId(action);
  const id = getActionReferenceId(action);
  if (!id) {
    return;
  }
  try {
    const favActions = await getActionHeadersForAnyEntity({
      actionTypes: [EntityActionType.Silverlight, EntityActionType.WebForm],
      typeName: ACTIONS_FOR_ACTIONS_HEADER_TYPE,
      entityID: id,
    });

    if (favActions && favActions.length) {
      const favAction = favActions[0];
      // oof... have to stop converting _somewhere_ for this particular effort...
      performAction(el, getActionName(favAction), entityId, favAction.Id);
    }
  } catch (error) {
    console.error('failed to execute favorite action', error);
  }
}

export async function executeActionByClientActionId(
  clientActionId: string,
  typeName: string,
  entityId: string,
  actionTypes = [EntityActionType.Mvc]
) {
  try {
    const action = await getActionByClientActionId({
      actionTypes,
      clientActionId,
      entityID: entityId,
      typeName,
    });

    if (action) {
      await performAction(null, action.Nm, clientActionId, action.Id, null, undefined, false, undefined, action.Tp);
    }
  } catch (error) {
    console.error('Failed to execute action by client action ID', error);
  }
}

export async function runBaseAction(
  entityId: string,
  baseAction: ActionHeader,
  entityType?: string,
  actionElement: HTMLElement | string = ''
): Promise<void> {
  if (isNavigateAction(baseAction) && baseAction.Url) {
    window.open(baseAction.Url, '_blank');
  } else {
    //TODO: get rid of openAction here
    if (entityType && entityType.length) {
      await performAction(
        actionElement,
        baseAction.Nm,
        entityId,
        baseAction.Id,
        undefined,
        undefined,
        undefined,
        undefined,
        entityType
      );
    } else {
      await performAction(actionElement, baseAction.Nm, entityId, baseAction.Id);
    }
  }
}

export interface IActionHandler {
  (action?: BaseActionType | ActionHeader): void;
}

export function getActionLinksForActions(
  actions: BaseActionType[] | ActionHeader[] = [],
  handler?: IActionHandler
): IActionLinksPair {
  // TODO this model is such a mess, can we consider just having a tidier nested structure from the server?
  const originalActionsById = new Map<string, BaseActionType | ActionHeader>(); // TODO does this make this stateful? :o
  const onActionClick = (id: string) => {
    const action = originalActionsById.get(id);
    handler?.(action);
    action && actionLinkHandler(action); // TODO no idea on the isGroup thing...
  };

  const items: IActionLinkItems = {
    current: [],
    nested: new Map(),
  };

  /* analog is actionmenumodel.ts#generateMenuElements,
   which sorts:
   - all actions by getCategoryName, then getOrder, getActionName
   - then action group names _purely_ alphabetically
   - then actions within groups only by order and name

   I think if we're going to sort by priority, order, and name, that should be consistent everywhere
   - 
  */
  actions = actions.sort(compareActions);

  // first we need to add fake action links for Favorites to have them on top
  actions.forEach(act => {
    const isFavorite = getActionIsFavorite(act);
    if (isFavorite) {
      const item = createLinkFromAction(act, onActionClick);
      items.current?.push({ ...item, label: getActionFavoriteName(act), emphasized: true });
    }
  });

  actions.forEach(act => {
    originalActionsById.set(act.Id, act);
    const category = getActionCategory(act) || '';
    const subCategory = getActionSubCategory(act);

    const item = createLinkFromAction(act, onActionClick);
    if (category) {
      // if this goes deeper, add recursion...
      let nestedItems: IActionLinkItems = ensureNestedLinks(items, category);
      if (subCategory) {
        let twiceNestedItems = ensureNestedLinks(nestedItems, subCategory);
        twiceNestedItems.current!.push(item);
      } else {
        nestedItems.current!.push(item);
      }
    } else {
      items.current?.push(item);
    }
  });

  return { originalActionsById, actionLinks: items };
}

function createLinkFromAction(act: BaseActionType | ActionHeader, onActionClick: (arg?: any) => void) {
  const item: IActionLinkItem = {
    label: getActionName(act),
    value: act.Id,
    onClick: onActionClick,
    order: getActionOrder(act),
    displayType: getDisplayType(act),
    emphasized: isPrimary(act),
    readonly: isReadonlyAction(act),
    isSeam: actionIsSeparator(act),
    isFavorite: getActionIsFavorite(act),
    canEditFavorite: getActionCanEditFavorite(act),
  };
  return item;
}

export function defaultActionHandler(action: BaseActionType | ActionHeader, folderId: string, isGroupAction = false) {
  const entities = (action as any).Entities;
  const currentFolderId = folderId || getFolderId(usePortalStore.getState()); // TODO code split settings [DT-037134] folderStore ref should be enough, I would think.
  const className = getActionClassName(action);
  const openActionMode = getActionMode(action);
  const forMobile = getIsMobile();
  if (isGroupAction && entities) {
    return performAction(
      null,
      getActionName(action),
      JSON.stringify(entities),
      action.Id,
      currentFolderId,
      undefined,
      forMobile,
      undefined,
      className,
      isGroupAction
    );
  } else {
    if (getActionUrl(action)) {
      window.open(getActionUrl(action), '_blank');
      // hideExistingMenu(); // do we actually need a way to do this React side? OverlayStore?
    } else {
      return performAction(
        null,
        getActionName(action),
        // is entityId not _always_ in the action id? why is legacy code passing it down from HTML?,
        getEntityId(action),
        action.Id,
        openActionMode,
        undefined,
        forMobile,
        undefined,
        className,
        isGroupAction
      );
    }
  }
}

function actionLinkHandler(action: BaseActionType | ActionHeader, isGroupAction = false) {
  defaultActionHandler(action, getFolderId(usePortalStore.getState()), isGroupAction); // TODO code split settings [DT-037134]
}

function getActionCategory(act: BaseActionType | ActionHeader) {
  // cheating with any since action type hierarchy is so diverse...
  return act.Ca || (act as any).Category;
}

function getActionSubCategory(act: BaseActionType | ActionHeader) {
  // cheating with any since action type hierarchy is so diverse...
  return act.SCa || (act as any).SubCategory;
}

function getActionIsFavorite(act: BaseActionType | ActionHeader) {
  return !!act.Fv?.length ?? !!(act as any).IsFavorite;
}
function getActionFavoriteName(act: BaseActionType | ActionHeader) {
  return act.Fv ?? (act as any).FavoriteName ?? getActionName(act);
}
function getActionCanEditFavorite(act: BaseActionType | ActionHeader) {
  return act.CeFv ?? (act as any).CeFv;
}

function getActionOrder(act: BaseActionType | ActionHeader) {
  // cheating with any since action type hierarchy is so diverse...
  return act.Or || (act as any).Order;
}

function getActionName(act: BaseActionType | ActionHeader) {
  return act.Nm || (act as any).Name; // TODO or what else, based on the Action Type
}

function getActionUrl(act: BaseActionType | ActionHeader) {
  return isNavigateAction(act) ? act.Url : '';
}

function getActionClassName(act: BaseActionType | ActionHeader) {
  // some action type will have a Url...
  const typos = (act as any).EntityTypeInfos;
  return typos?.EntityTypeName || (act as any).EntityTypeName || getEntityTypeFromActionId(act) || '';
}

export function isPrimary(actionHeader: ActionHeader): boolean {
  return getDisplayType(actionHeader) === ActionDisplayType.Primary;
}

export function isSecondary(actionHeader: ActionHeader): boolean {
  return getDisplayType(actionHeader) === ActionDisplayType.Secondary;
}

export function isNoneDisplayType(actionHeader: ActionHeader): boolean {
  return getDisplayType(actionHeader) === ActionDisplayType.None;
}

export function getActionMode(action: ActionHeader) {
  let openActionMode: NavigateType | null = null;

  if (isNavigateAction(action)) {
    const { navigateType } = parseNavigationAction(action);
    openActionMode = navigateType;
  }

  return openActionMode;
}

export function isNavigateAction(actionHeader: ActionHeader): actionHeader is NavigateActionHeader {
  return 'Ni' in actionHeader;
}

export function parseNavigationAction(
  action: NavigateActionHeader
): {
  actionFolderId: string;
  pageName: string;
  navigateType: NavigateType;
} {
  const [actionFolderId, pageName, navigateTypeStr] = action.Ni.split('|');

  return {
    actionFolderId,
    pageName,
    navigateType: parseInt(navigateTypeStr),
  };
}

export function isNewWindowNavigationType(arg: NavigateType | string | number) {
  if (typeof arg === 'string') {
    return parseInt(arg, 10) === NavigateType.NewWindow;
  }
  return arg === NavigateType.NewWindow;
}

export function getDisplayType(action: ActionHeader | BaseActionType): ActionDisplayType {
  let result = ActionDisplayType.None;

  // if it has it:
  if (isBaseActionType(action)) {
    return (action as BaseActionType).DisplayType;
  }

  // else just parse the flag:
  if (action.Flg != null && action.Flg.length >= 2) {
    result =
      action.Flg[2] === '1'
        ? ActionDisplayType.Primary
        : action.Flg[2] === '2'
        ? ActionDisplayType.Secondary
        : ActionDisplayType.None;
  }

  return result;
}

/**
 * Compare actions for sorting. Really this is exported so we can code-test it.
 * @param a action a
 * @param b action b
 * @returns sort integer
 */
export function compareActions(a: BaseActionType | ActionHeader, b: BaseActionType | ActionHeader) {
  // in the end we might need to sort these _after_ grouping by category...
  // but it seems like moving into categories after sorting would produce same results

  // legacy used Enumerable library:
  // actions = Enumerable
  //   .from(actions)
  //   .orderBy(getCategoryName)
  //   .thenBy(getOrder)
  //   .thenBy(getActionName)
  //   .toArray();

  // ones with categories above ones without:
  if (a.Ca && !b.Ca) return -1;
  if (b.Ca && !a.Ca) return 1;

  if (a.Ca && b.Ca && a.Ca > b.Ca) return 1;
  if (a.Ca && b.Ca && a.Ca < b.Ca) return -1;

  // ones with subcategories above ones without:
  if (a.SCa && !b.SCa) return -1;
  if (b.SCa && !a.SCa) return 1;

  // we'll add subcategory here ase well, so they're all in category, then subcategory
  if (a.SCa && b.SCa && a.SCa > b.SCa) return 1;
  if (a.SCa && b.SCa && a.SCa < b.SCa) return -1;

  // groups will end up after non-groups in ActionLinks, but primary actions go first inside or outside a group
  if (isPrimary(a) && !isPrimary(b)) return -1;
  if (!isPrimary(a) && isPrimary(b)) return 1;

  // .thenBy(getOrder)
  if (a.Or > b.Or) return 1;
  if (a.Or < b.Or) return -1;
  // .thenBy(getActionName)
  if (a.Nm > b.Nm) return 1;
  if (a.Nm < b.Nm) return -1;

  // all other things being equal...
  return 0;
}

function ensureNestedLinks(items: IActionLinkItems, nestedName: string) {
  let nestedItems: IActionLinkItems = items.nested?.get(nestedName) || {
    current: [],
    nested: new Map(),
  };
  items.nested = items.nested ? items.nested : new Map();
  if (!items.nested.get(nestedName)) {
    // assign an empty item for each nestedName if it's missing
    items.nested.set(nestedName, nestedItems);
  }
  return nestedItems;
}

export function getActionTypeName(act: BaseActionType | ActionHeader, short = false) {
  // this was how it is in legacy...
  const typeName = act.Id.split(ACTION_ID_SEPARATOR)[2];
  return short ? typeName.substr(typeName.lastIndexOf('.') + 1) : typeName;
}

export function getEntityId(act: BaseActionType | ActionHeader) {
  // this was how it is in legacy...
  return act.Id.split(ACTION_ID_SEPARATOR)[1];
}

function getEntityTypeFromActionId(act: BaseActionType | ActionHeader) {
  // is this not always in the action id?
  return act.Id.split(ACTION_ID_SEPARATOR)[2];
}

export function getActionReferenceId(act: BaseActionType | ActionHeader) {
  // is this really odd to anyone else?
  const entityType = getActionTypeName(act, true);
  const name = getActionName(act);
  const category = getActionCategory(act);
  const subCategory = getActionSubCategory(act);

  return `${name}:${category == null ? 'none' : category}:${subCategory == null ? 'none' : subCategory}:${entityType}`;
}

export function isMobileAction(action: ActionHeader): boolean {
  return (
    !isCreateElementAction(action) &&
    !isViewElementPreviewAction(action) &&
    stringContains(action.HT, 'M') &&
    doesAllowSingleActions(action) &&
    isPrimary(action)
  );
}

export function filterMobileActions(actions: ActionHeader[]): ActionHeader[] {
  return (actions || []).filter(isMobileAction);
}

export function isPortalAction(action: ActionHeader): boolean {
  return stringContains(action.HT, 'H');
}

export function forPortalActionSection(actionHeader: ActionHeader): boolean {
  return actionHeader && actionHeader.Pa;
}

export function isDefaultGridAction(actionHeader: ActionHeader): boolean {
  return actionHeader.Flg != null && actionHeader.Flg.length > 0 ? actionHeader.Flg[0] === '1' : false;
}

/** Just for logical readability */
export function isNotDefaultGridAction(actionHeader: ActionHeader) {
  return !isDefaultGridAction(actionHeader);
}

//NOTE: next methods are implemented on Server Side in ActionHeaderData.cs as well

export function doesAllowSingleActions(action: ActionHeader | BaseActionType): boolean {
  return (
    (action as BaseActionType).AllowSingleActions ||
    (action.Flg != null && action.Flg.length > 1 ? action.Flg[1] === '1' : false)
  );
}

export function isGetFileAction(actionHeader: ActionHeader): boolean {
  return actionHeader.Flg != null && actionHeader.Flg.length >= 5 ? actionHeader.Flg[5] === '1' : false;
}

export function isAddSubFolderWithBehavior(actionHeader: ActionHeader): boolean {
  return actionHeader.Flg != null && actionHeader.Flg.length >= 7 ? actionHeader.Flg[7] === '1' : false;
}

export function isCreateRootFolderAction(actionHeader: ActionHeader): boolean {
  return actionHeader.Flg != null && actionHeader.Flg.length >= 6 ? actionHeader.Flg[6] === '1' : false;
}

export function isGetTitleDescriptionAndFileAction(actionHeader: ActionHeader): boolean {
  return actionHeader.Flg != null && actionHeader.Flg.length >= 4 ? actionHeader.Flg[4] === '1' : false;
}

export function isCreateElementAction(actionHeader: ActionHeader): boolean {
  return actionHeader.Flg != null && actionHeader.Flg.length >= 8 ? actionHeader.Flg[8] === '1' : false;
}

export function isViewElementPreviewAction(actionHeader: ActionHeader): boolean {
  return actionHeader.Flg != null && actionHeader.Flg.length >= 3 ? actionHeader.Flg[3] === '1' : false;
}

export function isReadonlyAction(actionHeader: ActionHeader): boolean {
  return actionHeader.Tp === 'DecisionsFramework.ServiceLayer.Actions.Common.ReadonlyAction';
}

//TODO alex: when are we able to get here BaseActionType?
export function isGroupAction(action: ActionHeader | BaseActionType) {
  if (!(action as BaseActionType).AllowGroupActions) {
    return false;
  }

  if (action.Flg && action.Flg.length > 9 && action.Flg[9] === '0') {
    return false;
  }

  const mvcActionHandler = (action as BaseActionType).MvcActionHandler;
  const handlerPresent =
    mvcActionHandler != null && SupportedGroupActionHandlers.includes(mvcActionHandler.HandlerName);
  return handlerPresent;
}

export function isActionSupported(action: ActionHeader | BaseActionType, isGroupActions?: boolean) {
  let htAction = false;

  if ((action as BaseActionType).MvcActionHandler != null) {
    htAction = true;
  } else if (action.HT && isPortalAction(action)) {
    htAction = true;
  }

  return isGroupActions ? isGroupAction(action) && htAction : htAction;
}

const SupportedGroupActionHandlers = [
  'AddRootFolderActionHandler',
  'AddSubFolderActionHandler',
  'ApproveRejectActionHandler',
  'CallRestApiActionHandler',
  'ConfirmActionHandler',
  'ConfirmGoToFolderActionHandler',
  'DeleteEntityActionHandler',
  'DownloadFileActionHandler',
  'ExportEntitiesActionHandler',
  'FolderBrowseSearchActionHandler',
  'FolderEntityBrowseSearchHandler',
  'GetBoolActionHandler',
  'GetDateTimeActionHandler',
  'GetDecimalActionHandler',
  'GetFileActionHandler',
  'GetIntActionHandler',
  'GetNullableDecimalActionHandler',
  'GetStringActionHandler',
  'GetTimeSpanActionHandler',
  'GetTitleAndDescriptionActionHandler',
  'GetTitleDescriptionAndFileHandler',
  'MakeEnumSelectionActionHandler',
  'MakeMultiSelectionActionHandler',
  'MakeObjectSelectionActionHandler',
  'MakeSelectionSearchActionHandler',
  'MakeStringSelectionActionHandler',
  'ManageTagsActionHandler',
  'PickAccountsActionHandler',
  'PickGroupsActionHandler',
  'ReorderItemActionHandler',
  'RunFolderFlowGroupActionHandler',
  'SelectEntityActionHandler',
  'SelectFolderActionHandler',
  'SendEmailActionHandler',
  'SimpleActionHandler',
  'MakeSelectionActionHandler',
  'SetFormsTitleActionHandler',
  'WarningBeforeDeleteActionHandler',
];

export interface IActionFilterOptions {
  readonly folderId?: string;
  readonly entityId?: string;
  readonly isGroupActions?: boolean;
  readonly showNavigationInNewWindowOnly?: boolean;
  readonly ignoreNavigation?: boolean;
  readonly ignoreParentNavigation?: boolean;
  readonly onlySingleAllowActions?: boolean; // was this the intent of "isGroupActions" or slightly different nuance?
  readonly isMobile?: boolean;
}

export function filterActions<T extends ActionHeader | GroupAction>(actions: T[], options?: IActionFilterOptions): T[] {
  if (!actions || actions.length === 0) {
    return [];
  }

  const filtered = actions.filter(action => shouldAdd(getActionHeader(action, options?.isGroupActions), options));

  return filtered;
}

export function createNoActions() {
  return {
    Ca: '',
    Flg: '010000010',
    Fv: null,
    Id: 'no action',
    Nm: 'No Actions',
    Or: 0,
    SCa: null,
    Tp: 'NoAction',
    Tr: false,
    IsDummy: true,
  };
}

export function getActionHeader(action: ActionHeader | GroupAction, isGroupActions?: boolean): ActionHeader {
  if (isGroupActions) {
    return (action as GroupAction).Action;
  }

  return action as ActionHeader;
}

export function actionIsSeparator(actionHeader: ActionHeader): boolean {
  return actionHeader.Flg === 'SEPERATOR';
}

function shouldAdd(action: ActionHeader, options?: IActionFilterOptions) {
  if (options && !!options.isMobile && isMobileAction(action) === false) {
    return false;
  }

  if (options?.onlySingleAllowActions && !doesAllowSingleActions(action)) {
    // TODO is this duplicating isGroupActions, just with a more specific/opposite/to-the-point name?
    return false;
  }

  if (actionIsSeparator(action)) {
    return true;
  }

  if (options && !options.isGroupActions && !doesAllowSingleActions(action)) {
    return false;
  }

  if (isNavigateAction(action)) {
    const { actionFolderId, navigateType } = parseNavigationAction(action as NavigateActionHeader);

    //filter for show in new window only actions
    if (options && options.showNavigationInNewWindowOnly) {
      if (navigateType !== NavigateType.NewWindow) {
        return actionFolderId !== options.entityId;
      }
    } else {
      //filter for ignore navigation action
      if (options && options.ignoreNavigation && actionFolderId === options.entityId) {
        return false;
      }
      //filter for ignore parent action
      if (options && options.ignoreParentNavigation && actionFolderId === options.folderId) {
        return false;
      }

      return !isDefaultGridAction(action);
    }
  }

  const isSupported = isActionSupported(action, options?.isGroupActions);

  return isSupported;
}

export function getActionMessage(folderId: string, entityId: string, action: BaseActionType) {
  return newActionMessage({
    FolderID: folderId,
    RefreshScope: action.RefreshScope,
    ActionName: action.Nm,
    ActionTypeName: action.Id.split('|!~|')[2],
    RequiresLeaseCheck: action.RequiresLeaseCheck,
    ActionUniqueName: action.Id,
    Entities: [getEntityActionInfo(folderId, action.Id.split('|!~|')[2], entityId, action.Id)],
  });
}

function getEntityActionInfo(
  folderId: string,
  entityClassName: string,
  entityID: string,
  actionID: string
): EntityActionInfo {
  return newEntityActionInfo({
    EntityClassName: entityClassName,
    FolderID: folderId,
    EntityID: entityID,
    ActionID: actionID,
  });
}

/**
 * Currently a module wrapper around the UMD global 'openAction' because we don't want UMD globals littering
 * this codebase. Eventually this will be the one that actually does the work.
 *
 * TODO the more I use this, the more all these args are a code-smell. Would be better to consume an object, which had clear and meaningful names.
 *
 * @param element target element (will probably remove this long-term)
 * @param actionName name of action
 * @param entityId
 * @param actionUniqueId
 * @param openActionMode
 * @param entityName
 * @param forMobile
 * @param controller
 * @param className
 * @param isGroupAction
 * @param forceActionNameAsDialogTitle
 */
export function performAction(
  element: HTMLElement | string | null,
  actionName: string,
  entityId: string | null,
  actionUniqueId: string,
  openActionMode?: any,
  entityName?: string,
  forMobile?: boolean,
  controller?: string,
  className?: string,
  isGroupAction?: boolean,
  forceActionNameAsDialogTitle?: boolean
) {
  /**
   * `openAction` is declared globally in web.core. please use performAction inside Decisions.FrontEnd instead.
   * @deprecated
   */

  type openAction = (
    element: HTMLElement | string | null,
    actionName: string,
    entityId: string | null,
    actionUniqueId: string,
    openActionMode?: any,
    entityname?: string,
    formobile?: boolean,
    controller?: string,
    className?: string,
    isGroupAction?: boolean,
    forceactionNameAsDialogTitle?: boolean
  ) => PromiseLike<void>; //RETURNS A JQUERY PROMISE :scream:

  if (IS_TEST) {
    console.log('HEY SPY!');
    return;
  }
  if (DEV_SERVER) {
    return console.log('firing action', actionName, 'entityId:', entityId, 'actionUniqueId', actionUniqueId);
  }

  return new Promise(async (resolve, reject) => {
    try {
      const result = await ((window as any).openAction as openAction)(
        element,
        actionName,
        entityId,
        actionUniqueId,
        openActionMode,
        entityName,
        forMobile,
        controller,
        className,
        isGroupAction,
        forceActionNameAsDialogTitle
      );
      resolve(result);
    } catch (ex) {
      reject(ex);
    }
  });
}
